import React, { useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
} from "@mui/material";
import { Search as SearchIcon, Upload as UploadIcon, Settings as SettingsIcon, Close as CloseIcon } from "@mui/icons-material";
import { fetchAggregation, deletePayments } from "./api";
import "./PaymentsTable.css";

// Custom hooks
import { usePayments } from "./hooks/usePayments";
import { useCategories } from "./hooks/useCategories";
import { useTableColumns } from "./hooks/useTableColumns";

// Components
import ManageCategoriesDialog from "./components/ManageCategoriesDialog";
import SummaryCards from "./components/SummaryCards";
import PaymentTableRow from "./components/PaymentTableRow";
import AggregationDialog from "./components/AggregationDialog";
import FileUpload from "./components/FileUpload";
import SettingsDialog from "./components/SettingsDialog";
import ConfirmDialog from "./components/ConfirmDialog";

export default function PaymentsTable() {
  const {
    payments,
    setPayments,
    loading,
    error,
    total,
    page,
    setPage,
    search,
    setSearch,
    sort,
    setSort,
    refetchPayments,
  } = usePayments();

  const {
    categories,
    categoryTree,
    managerOpen,
    setManagerOpen,
    handleUpdateCategoryTree,
    handleCategoryChange,
    CategoryConfirmDialog
  } = useCategories(refetchPayments);

  const {
    allColumns,
    orderedColumns,
    visibleColumns,
    toggleColumn,
    onDragEnd
  } = useTableColumns();

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);

  // Use backend data directly
  const displayedPayments = payments;

  // Aggregation dialog state
  const [aggregationOpen, setAggregationOpen] = useState(false);
  const [aggregationData, setAggregationData] = useState(null);
  const [aggregationLoading, setAggregationLoading] = useState(false);
  const [aggregationError, setAggregationError] = useState("");
  const [aggregationTitle, setAggregationTitle] = useState("");

  // Confirm dialog state for delete
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const toNaiveISOString = (d) => {
    if (!d) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // Handler for summary card aggregation
  const handleSummaryCardAggregate = async ({ type, start, end, days }) => {
    let title = "";
    if (type === "total") {
      title = "Total Aggregation";
    } else if (type === "past7") {
      title = "Past 7 Days Aggregation";
    } else if (type === "past30") {
      title = "Past 30 Days Aggregation";
    } else if (type === "custom") {
      title = "Custom Range Aggregation";
      if (!start || !end) return;
    }
    setAggregationTitle(title);
    setAggregationLoading(true);
    setAggregationOpen(true);
    setAggregationError("");
    try {
      let params = {};
      if (type === "past7" || type === "past30") {
        params.days = days;
      } else if (type === "custom") {
        params.start_date = toNaiveISOString(start);
        params.end_date = toNaiveISOString(end);
      }
      const data = await fetchAggregation(params);
      setAggregationData(data);
    } catch (err) {
      setAggregationError("Failed to fetch aggregation data.");
      setAggregationData(null);
    }
    setAggregationLoading(false);
  };

  const handleUploadSuccess = () => {
    refetchPayments();
    setUploadDialogOpen(false);
    setSummaryRefreshKey(k => k + 1); // trigger summary refresh
  };

  // Add missing state for dateRange
  const [dateRange, setDateRange] = useState([null, null]);

  // Add summary refresh key state
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);

  // Add missing handler for category change dialog
  const handleCategoryChangeWithDialog = (payment, value) => {
    handleCategoryChange(payment, value, payments, setPayments);
  };

  // Handle row selection
  const handleRowClick = (payment, event) => {
    if (event.ctrlKey || event.metaKey) {
      setSelectedIds((prev) =>
        prev.includes(payment.id)
          ? prev.filter((id) => id !== payment.id)
          : [...prev, payment.id]
      );
    } else if (event.shiftKey && selectedIds.length > 0) {
      const lastIndex = displayedPayments.findIndex((p) => p.id === selectedIds[selectedIds.length - 1]);
      const thisIndex = displayedPayments.findIndex((p) => p.id === payment.id);
      if (lastIndex !== -1 && thisIndex !== -1) {
        const [start, end] = [lastIndex, thisIndex].sort((a, b) => a - b);
        const rangeIds = displayedPayments.slice(start, end + 1).map((p) => p.id);
        setSelectedIds(Array.from(new Set([...selectedIds, ...rangeIds])));
      }
    } else {
      setSelectedIds([payment.id]);
    }
  };

  // Context menu handlers
  const handleRowContextMenu = (payment, event) => {
    event.preventDefault();
    if (!selectedIds.includes(payment.id)) {
      setSelectedIds([payment.id]);
    }
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleDeleteSelected = async () => {
    setConfirmDeleteOpen(true);
    handleCloseContextMenu();
  };

  const handleConfirmDelete = async () => {
    setConfirmDeleteOpen(false);
    if (selectedIds.length === 0) return;
    try {
      await deletePayments(selectedIds);
      setSelectedIds([]);
      refetchPayments();
      setSummaryRefreshKey(k => k + 1); // trigger summary refresh
    } catch (e) {
      alert("Failed to delete payments.");
    }
  };

  // Search handler: only set local value, don't trigger search
  const [searchInput, setSearchInput] = useState(search);

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchSubmit = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleSort = (field) => {
    setSort((prev) => {
      const newDirection = prev.field === field && prev.direction === "asc" ? "desc" : "asc";
      return { field, direction: newDirection };
    });
  };

const refetchPaymentsRef = React.useRef(refetchPayments);
React.useEffect(() => {
  refetchPaymentsRef.current = refetchPayments;
}, [refetchPayments]);

React.useEffect(() => {
  // use ref to avoid including `refetchPayments` in deps
  refetchPaymentsRef.current({ page, sortField: sort.field, sortDirection: sort.direction });
}, [page, sort]);

  if (error) {
    return (
      <div className="payments-container">
        <Typography color="error" sx={{ textAlign: 'center', mt: 4 }}>{error}</Typography>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ManageCategoriesDialog
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        categoryTree={categoryTree}
        onUpdate={handleUpdateCategoryTree}
        payments={payments}
      />
      <AggregationDialog
        open={aggregationOpen}
        onClose={() => setAggregationOpen(false)}
        data={aggregationData}
        loading={aggregationLoading}
        error={aggregationError}
        title={aggregationTitle}
      />

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: -0.01 }}>
            Import Payment Data
          </Typography>
          <IconButton onClick={() => setUploadDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FileUpload onSuccess={handleUploadSuccess} dialogOpen={uploadDialogOpen} />
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <SettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        setManagerOpen={setManagerOpen}
        dateRange={dateRange}
        setDateRange={setDateRange}
        allColumns={allColumns}
        orderedColumns={orderedColumns}
        visibleColumns={visibleColumns}
        toggleColumn={toggleColumn}
        categories={categories}
        refetchPayments={() => {
          refetchPayments();
          setSummaryRefreshKey(k => k + 1); // trigger summary refresh on custom payment
        }}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete ${selectedIds.length > 1 ? "Selected Payments" : "Payment"}`}
        description={`Are you sure you want to delete ${selectedIds.length} payment${selectedIds.length > 1 ? "s" : ""}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <div className="payments-container animate-in">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <h1>PayFlow</h1>
            <p>Track your transactions with a refined, modern interface</p>
          </div>
          <div className="header-actions">
            <Button
              variant="contained"
              className="btn-primary upload-btn"
              onClick={() => setSettingsDialogOpen(true)}
              startIcon={<SettingsIcon />}
            >
              Settings
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <Box sx={{ position: "relative", minHeight: "120px" }}>
          <SummaryCards
            dateRange={dateRange}
            setDateRange={setDateRange}
            onAggregate={handleSummaryCardAggregate}
            summaryRefreshKey={summaryRefreshKey}
            setSummaryRefreshKey={setSummaryRefreshKey}
          />
        </Box>

        {/* Search Bar and Upload Button */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Search transactions..."
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(0,0,0,0.4)' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: '12px',
                background: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.08)',
                  borderWidth: '2px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(102,126,234,0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#667eea',
                },
                height: '48px',
                fontSize: '15px',
                fontWeight: 500,
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearchSubmit}
            className="btn-primary upload-btn"
          >
            Search
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            className="btn-primary upload-btn"
          >
            Upload
          </Button>
        </Box>
        <div className="table-container animate-in">
          <div className="table-header">
            <h2>Transactions</h2>
            <span className="table-count">
              {total} {total === 1 ? 'item' : 'items'}
            </span>
          </div>
          <TableContainer sx={{ maxHeight: "65vh", position: "relative" }}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(255,255,255,0.7)",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={48} thickness={3.5} sx={{ color: '#667eea' }} />
              </Box>
            )}
            <DragDropContext onDragEnd={onDragEnd}>
              <Table className="payments-table" stickyHeader size="medium" aria-label="payments table">
                <TableHead>
                  <Droppable droppableId="columns" direction="horizontal">
                    {(provided) => (
                      <TableRow ref={provided.innerRef} {...provided.droppableProps}>
                        {orderedColumns.map((col, index) => {
                          if (!visibleColumns.has(col.id)) return null;
                          return (
                            <Draggable draggableId={col.id} index={index} key={col.id}>
                              {(dragProvided, snapshot) => (
                                <TableCell
                                  align={col.align}
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  className={snapshot.isDragging ? 'dragging' : ''}
                                  sx={{
                                    cursor: snapshot.isDragging ? 'grabbing' : 'pointer',
                                    '&:hover': {
                                      background: 'rgba(102,126,234,0.08)',
                                      color: '#667eea',
                                    }
                                  }}
                                  onClick={() => handleSort(col.id)}
                                >
                                  {col.label}
                                  {sort.field === col.id && (sort.direction === "asc" ? " ↑" : " ↓")}
                                </TableCell>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </TableRow>
                    )}
                  </Droppable>
                </TableHead>
                <TableBody>
                  {displayedPayments.map((payment) => (
                    <PaymentTableRow
                      key={payment.id}
                      payment={payment}
                      orderedColumns={orderedColumns}
                      visibleColumns={visibleColumns}
                      categories={categories}
                      onCategoryChange={handleCategoryChangeWithDialog}
                      selected={selectedIds.includes(payment.id)}
                      onRowClick={handleRowClick}
                      onRowContextMenu={handleRowContextMenu}
                    />
                  ))}
                  {displayedPayments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.size || 1} className="empty-state">
                        <div className="empty-state-icon">📭</div>
                        <div className="empty-state-text">No results found. Try adjusting your filters or upload some data.</div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </DragDropContext>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={Math.ceil(total / 50)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              size="large"
            />
          </Box>
        </div>
        {/* Context Menu for Delete */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={handleDeleteSelected} disabled={selectedIds.length === 0}>
            Delete {selectedIds.length > 1 ? "Selected Payments" : "Payment"}
          </MenuItem>
        </Menu>
      </div>
      <CategoryConfirmDialog />
    </LocalizationProvider>
  );
}
